import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/lib/useBrand";
import { toast, Toaster } from "sonner";
import {
  adminGetStats,
  adminListUsers,
  adminPatchUser,
  adminDeleteUser,
  adminListOffers,
  adminDeleteOffer,
  adminListWorkspaces,
  adminDeleteWorkspace,
  adminGetConfig,
  adminPatchConfig,
} from "@/lib/api";
import {
  Users,
  FileText,
  UsersThree,
  Gear,
  ChartBar,
  Trash,
  PencilSimple,
  MagnifyingGlass,
  Crown,
} from "@phosphor-icons/react";

const TABS = [
  { id: "overview", label: "Overview", icon: ChartBar },
  { id: "users", label: "Users", icon: Users },
  { id: "offers", label: "Offers", icon: FileText },
  { id: "workspaces", label: "Workspaces", icon: UsersThree },
  { id: "settings", label: "Feature flags", icon: Gear },
];

function StatCard({ label, value }) {
  return (
    <div className="bg-[#0F1F38] border border-white/5 rounded-lg p-5">
      <p className="text-[10px] tracking-[0.25em] uppercase text-[#9AA5B5] mb-2">
        {label}
      </p>
      <p className="font-display text-3xl font-light tracking-tighter text-[#F5F1E8]">
        {value}
      </p>
    </div>
  );
}

function OverviewTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    adminGetStats().then(setStats).catch(() => toast.error("Stats failed to load"));
  }, []);
  if (!stats) {
    return <div className="text-[#9AA5B5] text-sm">Loading stats…</div>;
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="admin-overview-grid">
      <StatCard label="Users" value={stats.users} />
      <StatCard label="Offers" value={stats.offers} />
      <StatCard label="Refined" value={stats.refined_offers} />
      <StatCard label="Workspaces" value={stats.workspaces} />
      <StatCard label="Paid Tx" value={stats.paid_transactions} />
    </div>
  );
}

function UsersTab() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async (query = q) => {
    setLoading(true);
    try {
      setData(await adminListUsers(query));
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(""); }, []);

  const onSetCredits = async (u) => {
    const next = window.prompt(`Set credits for ${u.email}`, String(u.credits ?? 0));
    if (next === null) return;
    const n = parseInt(next, 10);
    if (Number.isNaN(n) || n < 0) {
      toast.error("Enter a non-negative integer");
      return;
    }
    try {
      await adminPatchUser(u.user_id, { credits: n });
      toast.success(`Credits updated for ${u.email}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };
  const onDelete = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This removes all their offers + sessions.`)) return;
    try {
      await adminDeleteUser(u.user_id);
      toast.success("User removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9AA5B5]" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by email or name…"
            className="pl-9 bg-[#0F1F38] border-white/10 text-[#F5F1E8]"
            data-testid="admin-users-search"
          />
        </div>
        <span className="text-xs text-[#9AA5B5]">{data.total} total</span>
      </div>
      <div className="bg-[#0F1F38] border border-white/5 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-[10px] tracking-[0.2em] uppercase text-[#9AA5B5]">
            <tr>
              <th className="text-left px-4 py-3">User</th>
              <th className="text-left px-4 py-3">Credits</th>
              <th className="text-left px-4 py-3">Free used</th>
              <th className="text-left px-4 py-3">Created</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-6 text-[#9AA5B5] text-center">Loading…</td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-6 text-[#9AA5B5] text-center">No users.</td></tr>
            ) : data.items.map((u) => (
              <tr key={u.user_id} className="border-t border-white/5" data-testid={`admin-user-row-${u.user_id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[#F5F1E8]">{u.name || u.email}</span>
                    {u.is_admin && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#C9A961] bg-[#C9A961]/10 px-1.5 py-0.5 rounded">
                        <Crown className="h-3 w-3" /> Admin
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[#9AA5B5]">{u.email}</div>
                </td>
                <td className="px-4 py-3 font-mono text-[#C9A961]">{u.credits ?? 0}</td>
                <td className="px-4 py-3 text-xs text-[#9AA5B5]">{u.has_used_free_credit ? "yes" : "no"}</td>
                <td className="px-4 py-3 text-xs text-[#9AA5B5]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => onSetCredits(u)}
                    className="text-[#9AA5B5] hover:text-[#C9A961] p-1.5 mr-1"
                    title="Set credits"
                    data-testid={`admin-user-credits-${u.user_id}`}
                  >
                    <PencilSimple className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onDelete(u)}
                    className="text-[#9AA5B5] hover:text-[#ef4444] p-1.5"
                    title="Delete user"
                    data-testid={`admin-user-delete-${u.user_id}`}
                  >
                    <Trash className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OffersTab() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async (query = q) => {
    setLoading(true);
    try {
      setData(await adminListOffers(query));
    } catch {
      toast.error("Failed to load offers");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(""); }, []);

  const onDelete = async (o) => {
    if (!window.confirm(`Delete offer "${o.output?.headline?.slice(0, 60) || o.id}"?`)) return;
    try {
      await adminDeleteOffer(o.id);
      toast.success("Offer removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9AA5B5]" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by headline or input…"
            className="pl-9 bg-[#0F1F38] border-white/10 text-[#F5F1E8]"
            data-testid="admin-offers-search"
          />
        </div>
        <span className="text-xs text-[#9AA5B5]">{data.total} total</span>
      </div>
      <div className="bg-[#0F1F38] border border-white/5 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-[10px] tracking-[0.2em] uppercase text-[#9AA5B5]">
            <tr>
              <th className="text-left px-4 py-3">Headline</th>
              <th className="text-left px-4 py-3">Owner</th>
              <th className="text-left px-4 py-3">Refined</th>
              <th className="text-left px-4 py-3">Created</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-6 text-[#9AA5B5] text-center">Loading…</td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-6 text-[#9AA5B5] text-center">No offers.</td></tr>
            ) : data.items.map((o) => (
              <tr key={o.id} className="border-t border-white/5" data-testid={`admin-offer-row-${o.id}`}>
                <td className="px-4 py-3 text-[#F5F1E8] max-w-md truncate">{o.output?.headline || "Untitled"}</td>
                <td className="px-4 py-3 text-xs text-[#9AA5B5]">{o.owner_email || "—"}</td>
                <td className="px-4 py-3 text-xs text-[#9AA5B5]">{o.parent_id ? "yes" : "no"}</td>
                <td className="px-4 py-3 text-xs text-[#9AA5B5]">{o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => onDelete(o)}
                    className="text-[#9AA5B5] hover:text-[#ef4444] p-1.5"
                    title="Delete offer"
                    data-testid={`admin-offer-delete-${o.id}`}
                  >
                    <Trash className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function WorkspacesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListWorkspaces();
      setItems(data.items || []);
    } catch {
      toast.error("Failed to load workspaces");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (w) => {
    if (!window.confirm(`Delete workspace "${w.name}"? Offers will be detached, not deleted.`)) return;
    try {
      await adminDeleteWorkspace(w.workspace_id);
      toast.success("Workspace removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="bg-[#0F1F38] border border-white/5 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-white/[0.03] text-[10px] tracking-[0.2em] uppercase text-[#9AA5B5]">
          <tr>
            <th className="text-left px-4 py-3">Workspace</th>
            <th className="text-left px-4 py-3">Owner</th>
            <th className="text-left px-4 py-3">Members</th>
            <th className="text-left px-4 py-3">Offers</th>
            <th className="text-right px-4 py-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={5} className="px-4 py-6 text-[#9AA5B5] text-center">Loading…</td></tr>
          ) : items.length === 0 ? (
            <tr><td colSpan={5} className="px-4 py-6 text-[#9AA5B5] text-center">No workspaces.</td></tr>
          ) : items.map((w) => (
            <tr key={w.workspace_id} className="border-t border-white/5" data-testid={`admin-workspace-row-${w.workspace_id}`}>
              <td className="px-4 py-3 text-[#F5F1E8]">{w.name}</td>
              <td className="px-4 py-3 text-xs text-[#9AA5B5]">{w.owner_email}</td>
              <td className="px-4 py-3 font-mono text-[#9AA5B5]">{w.member_count}</td>
              <td className="px-4 py-3 font-mono text-[#9AA5B5]">{w.offer_count}</td>
              <td className="px-4 py-3 text-right">
                <button
                  onClick={() => onDelete(w)}
                  className="text-[#9AA5B5] hover:text-[#ef4444] p-1.5"
                  title="Delete workspace"
                  data-testid={`admin-workspace-delete-${w.workspace_id}`}
                >
                  <Trash className="h-4 w-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SettingsTab() {
  const { refresh } = useAuth();
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminGetConfig().then(setConfig).catch(() => toast.error("Config failed to load"));
  }, []);

  const onToggle = async (key, next) => {
    setSaving(true);
    try {
      const updated = await adminPatchConfig({ [key]: next });
      setConfig({ ...config, ...updated });
      toast.success(`Saved — ${key} = ${next ? "on" : "off"}`);
      // Refresh app-wide config so other tabs/pages react immediately
      refresh && refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!config) return <div className="text-[#9AA5B5] text-sm">Loading settings…</div>;

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="bg-[#0F1F38] border border-white/5 rounded-lg p-5 flex items-center justify-between gap-6">
        <div>
          <p className="font-display font-bold text-[#F5F1E8] mb-1">Pricing tab</p>
          <p className="text-xs text-[#9AA5B5]">
            Show the public /pricing page + nav links + Buy-credits buttons.
            Turn this on when you&apos;re ready to start selling credit packs.
          </p>
        </div>
        <Switch
          checked={!!config.show_pricing}
          disabled={saving}
          onCheckedChange={(v) => onToggle("show_pricing", v)}
          data-testid="admin-toggle-show-pricing"
        />
      </div>

      <div className="bg-[#0F1F38] border border-white/5 rounded-lg p-5">
        <p className="font-display font-bold text-[#F5F1E8] mb-1">Free credits per new signup</p>
        <p className="text-xs text-[#9AA5B5] mb-2">
          Controlled via the <code className="text-[#C9A961]">FREE_CREDITS_DEFAULT</code> env var on the host.
          Currently <span className="text-[#C9A961] font-mono">{config.free_credits_default}</span>.
        </p>
        <p className="text-xs text-[#9AA5B5]">
          To change, update the env var on Railway and redeploy. Existing users keep their current balance.
        </p>
      </div>

      <div className="bg-[#0F1F38] border border-white/5 rounded-lg p-5">
        <p className="font-display font-bold text-[#F5F1E8] mb-1">Admins</p>
        <p className="text-xs text-[#9AA5B5]">
          <span className="text-[#C9A961] font-mono">{config.admin_emails_count}</span> email(s) granted admin access via the
          <code className="text-[#C9A961] mx-1">ADMIN_EMAILS</code> env var on the host.
        </p>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const { name: appName } = useBrand();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    if (!user.is_admin) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading || !user || !user.is_admin) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-2 border-[#C9A961] border-t-transparent animate-spin" />
      </div>
    );
  }

  const ActiveTab = {
    overview: OverviewTab,
    users: UsersTab,
    offers: OffersTab,
    workspaces: WorkspacesTab,
    settings: SettingsTab,
  }[tab];

  return (
    <div className="min-h-screen bg-[#0A1628] text-[#F5F1E8] flex flex-col">
      <Navbar />
      <Toaster theme="dark" position="top-right" />

      <div className="flex-1 max-w-7xl mx-auto w-full px-6 md:px-10 py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#C9A961] mb-2">
            Owner console
          </p>
          <h1 className="font-display text-4xl md:text-5xl font-light tracking-tighter leading-none">
            <span className="font-black">Admin</span>
          </h1>
          <p className="mt-3 text-sm text-[#9AA5B5] max-w-2xl">
            Manage every user, offer, workspace, and feature flag in {appName}.
          </p>
        </motion.div>

        <div className="mt-10 flex flex-wrap gap-2 border-b border-white/5">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={
                "px-4 py-2.5 text-sm flex items-center gap-2 border-b-2 -mb-px transition-colors " +
                (tab === id
                  ? "border-[#C9A961] text-[#F5F1E8]"
                  : "border-transparent text-[#9AA5B5] hover:text-white")
              }
              data-testid={`admin-tab-${id}`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        <div className="mt-8" data-testid={`admin-pane-${tab}`}>
          <ActiveTab />
        </div>
      </div>

      <Footer />
    </div>
  );
}
